import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Check, AlertCircle, Send, Smartphone, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/PushNotification.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detect iOS Safari PWA install state
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isStandalonePWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

const PushNotificationToggle = () => {
  const { token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [supportReason, setSupportReason] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [devicesPanelOpen, setDevicesPanelOpen] = useState(false);
  const [devices, setDevices] = useState([]);

  const checkSupport = useCallback(async () => {
    // iOS Safari requires the PWA to be installed to the Home Screen
    if (isIOS() && !isStandalonePWA()) {
      setIsSupported(false);
      setSupportReason('ios-needs-install');
      setIsLoading(false);
      return;
    }

    // Check if browser supports push notifications
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setIsSupported(false);
      setSupportReason('unsupported-browser');
      setIsLoading(false);
      return;
    }

    setIsSupported(true);
    setSupportReason('');

    // Check current subscription status - BOTH browser AND server
    try {
      const registration = await navigator.serviceWorker.ready;
      const browserSubscription = await registration.pushManager.getSubscription();

      let serverHasSubscription = false;
      if (token && browserSubscription) {
        try {
          const response = await fetch(`${API_URL}/api/push/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            serverHasSubscription = data.enabled;
          }
        } catch (e) {
          console.log('Could not check server subscription status');
        }
      }

      setIsSubscribed(!!browserSubscription && serverHasSubscription);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }

    setIsLoading(false);
  }, [token]);

  const fetchDevices = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/push/devices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (e) {
      console.error('Failed to fetch devices:', e);
    }
  }, [token]);

  useEffect(() => {
    checkSupport();
  }, [checkSupport]);

  useEffect(() => {
    if (devicesPanelOpen) fetchDevices();
  }, [devicesPanelOpen, fetchDevices]);

  const subscribe = async () => {
    setIsLoading(true);
    setError(null);

    if (!token) {
      setError('You must be logged in to enable push notifications. Please log in to the admin or patient portal first.');
      setIsLoading(false);
      return;
    }

    try {
      // Fetch VAPID public key
      const keyResponse = await fetch(`${API_URL}/api/push/vapid-public-key`);
      if (!keyResponse.ok) {
        throw new Error('Push notifications not configured on server. Contact support.');
      }
      const { publicKey } = await keyResponse.json();

      // Request notification permission with detailed error reporting
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        throw new Error(
          isIOS()
            ? 'Notifications blocked. Open iPhone Settings → Notifications → Safari and allow notifications for this site.'
            : 'Notifications blocked. Click the lock icon in your browser\u2019s address bar and allow notifications for this site.'
        );
      }
      if (permission !== 'granted') {
        throw new Error('Notification permission was not granted. Please try again and tap \u201CAllow\u201D.');
      }

      const registration = await navigator.serviceWorker.ready;
      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      // Unsubscribe any stale subscription for a fresh start
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) {
        await existingSub.unsubscribe();
      }

      // Subscribe with detailed error handling
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
      } catch (subErr) {
        if (subErr.name === 'NotAllowedError') {
          throw new Error('Push registration was blocked by the browser. Make sure notifications are allowed in site settings.');
        }
        if (subErr.name === 'AbortError') {
          throw new Error('Push service is unavailable right now. Check your internet connection and try again.');
        }
        throw new Error(`Subscription failed: ${subErr.message || subErr.name}`);
      }

      // POST to backend
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server rejected the subscription (${response.status}): ${errorText.slice(0, 200)}`);
      }

      setIsSubscribed(true);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // Refresh device list if panel is open
      if (devicesPanelOpen) fetchDevices();
    } catch (err) {
      console.error('[CADENCE Push] Subscribe error:', err);
      setError(err.message || String(err));
    }

    setIsLoading(false);
  };

  const sendTestNotification = async () => {
    if (!token) return;

    setIsSendingTest(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'no_subscription' || (data.details && data.details.sent === 0)) {
          setError('Test push delivery failed. The subscription is registered but the browser\u2019s push service didn\u2019t accept it. Try waiting 30 seconds and tapping the test button again, or re-enable: tap the bell once to disable, then tap again to re-subscribe.');
        } else {
          setTestSent(true);
          setTimeout(() => setTestSent(false), 3000);
        }
      } else {
        setError(`Test push failed (${response.status})`);
      }
    } catch (err) {
      console.error('Test notification error:', err);
      setError(err.message || 'Test push failed');
    }
    setIsSendingTest(false);
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {}
          })
        });
      }

      setIsSubscribed(false);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message);
    }

    setIsLoading(false);
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  // Unsupported / iOS install required
  if (!isSupported) {
    const iosInstall = supportReason === 'ios-needs-install';
    return (
      <div className="push-notification-wrapper">
        <button
          className="push-notification-toggle unsupported"
          title={iosInstall
            ? 'Install the CADENCE app to Home Screen to enable push notifications on iPhone'
            : 'Push notifications not supported in this browser'}
          onClick={() => setDevicesPanelOpen(v => !v)}
          data-testid="push-notification-toggle"
        >
          <BellOff size={18} />
        </button>
        {devicesPanelOpen && (
          <div className="push-devices-panel" data-testid="push-devices-panel">
            <h4>Push Notifications Unavailable</h4>
            {iosInstall ? (
              <>
                <p className="push-panel-body">
                  <strong>iPhone/iPad requires the app to be installed.</strong>
                </p>
                <ol className="push-panel-steps">
                  <li>In Safari, tap the <strong>Share</strong> button (square with up arrow).</li>
                  <li>Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>.</li>
                  <li>Open CADENCE from the new Home Screen icon.</li>
                  <li>Return to this dashboard and tap the bell again.</li>
                </ol>
              </>
            ) : (
              <p className="push-panel-body">
                Your browser doesn&apos;t support Web Push. Try the latest Chrome, Edge, Firefox, or Safari (on iOS 16.4+).
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="push-notification-wrapper">
      <button
        className={`push-notification-toggle ${isSubscribed ? 'subscribed' : ''} ${showSuccess ? 'success' : ''} ${error ? 'error' : ''}`}
        onClick={isSubscribed ? unsubscribe : subscribe}
        disabled={isLoading}
        title={error ? `Error: ${error}` : (isSubscribed ? 'Disable push notifications' : 'Enable push notifications')}
        data-testid="push-notification-toggle"
      >
        {isLoading ? (
          <span className="loading-spinner" />
        ) : showSuccess ? (
          <Check size={18} />
        ) : error ? (
          <AlertCircle size={18} />
        ) : isSubscribed ? (
          <Bell size={18} className="bell-active" />
        ) : (
          <BellOff size={18} />
        )}
      </button>

      {/* Devices / Status panel trigger */}
      <button
        className="push-devices-btn"
        onClick={() => setDevicesPanelOpen(v => !v)}
        title="Show subscribed devices"
        data-testid="push-devices-btn"
        type="button"
      >
        <Smartphone size={14} />
        <ChevronDown size={12} />
      </button>

      {/* Test notification button - only show when subscribed */}
      {isSubscribed && (
        <button
          className={`push-test-btn ${testSent ? 'sent' : ''}`}
          onClick={sendTestNotification}
          disabled={isSendingTest}
          title="Send test notification to this device"
          data-testid="push-test-btn"
          type="button"
        >
          {isSendingTest ? (
            <span className="loading-spinner small" />
          ) : testSent ? (
            <Check size={14} />
          ) : (
            <Send size={14} />
          )}
        </button>
      )}

      {/* Inline error message — full-width banner, dismissible */}
      {error && (
        <div className="push-error-message" role="alert" data-testid="push-error-message">
          <AlertCircle size={22} />
          <div>
            <strong>Push notifications failed:</strong> {error}
          </div>
          <button
            type="button"
            className="push-error-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
            data-testid="push-error-dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Devices panel */}
      {devicesPanelOpen && (
        <div className="push-devices-panel" data-testid="push-devices-panel">
          <h4>Subscribed Devices <span className="push-count-badge">{devices.length}</span></h4>
          {devices.length === 0 ? (
            <p className="push-panel-body push-panel-empty">
              <strong>No devices registered yet.</strong><br />
              Tap the bell on each device (phone, laptop, tablet) you want alerts on. On iPhone, add the app to your Home Screen first.
            </p>
          ) : (
            <ul className="push-devices-list">
              {devices.map((d, i) => (
                <li key={i} className="push-device-item">
                  <span className="push-device-type">{d.device_type}</span>
                  {d.user_email && <span className="push-device-email">{d.user_email}</span>}
                  {d.subscribed_at && (
                    <span className="push-device-date">
                      {new Date(d.subscribed_at).toLocaleDateString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <button
            className="push-refresh-btn"
            onClick={fetchDevices}
            type="button"
            data-testid="push-refresh-devices"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};

export default PushNotificationToggle;
